import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { shipmentsApi } from '@/services/api';

/**
 * Dealer-facing read-only view of consolidated shipments. Dealers can see
 * which of their orders are grouped, the total freight + their per-order share,
 * the carrier, BOL/tracking, and the shipment's progress. They cannot modify
 * shipments — that's admin-only.
 */

interface ShipmentRow {
  id: string;
  status: string;
  freightTotalAmount: number | null;
  selectedCarrierName: string | null;
  bolNumber: string | null;
  proNumber: string | null;
  trackingNumber: string | null;
  freightTrackingUrl: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  orderCount: number;
  totalItemCount: number;
  createdAt: string;
  orders?: Array<{
    id: string;
    orderNumber: string;
    sidemark: string | null;
    status: string;
    itemCount: number;
    freightShare: number | null;
  }>;
  boxes?: Array<{
    id: string;
    length: number | null;
    width: number | null;
    height: number | null;
    weight: number | null;
  }>;
}

const STATUS_LABEL: Record<string, string> = {
  building: 'Building',
  quoted: 'Quoted',
  booked: 'Booked',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_COLOR: Record<string, string> = {
  building: 'bg-amber-100 text-amber-800 border-amber-300',
  quoted: 'bg-blue-100 text-blue-800 border-blue-300',
  booked: 'bg-purple-100 text-purple-800 border-purple-300',
  shipped: 'bg-green-100 text-green-800 border-green-300',
  delivered: 'bg-gray-200 text-gray-700 border-gray-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
};

const STATUS_HINT: Record<string, string> = {
  building: 'Orders are being grouped — freight not yet quoted.',
  quoted: 'Freight rate is locked in. Awaiting carrier booking.',
  booked: 'Booked with carrier. Awaiting pickup.',
  shipped: 'On the way to you.',
  delivered: 'Delivered.',
  cancelled: 'This shipment was cancelled — orders are back on standalone freight.',
};

function fmt(n: number | null | undefined) {
  if (n == null) return '—';
  return '$' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
}

export const MyShipmentsPage: React.FC = () => {
  const [shipments, setShipments] = useState<ShipmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await shipmentsApi.listMy(showCompleted);
      setShipments(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showCompleted]);

  const toggleExpanded = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Shipments</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Every order that's booked or on its way — whether it ships on its own or bundled with others. Bundled shipments split freight proportionally by item count across their orders.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="rounded"
          />
          Show delivered + cancelled
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded mb-4">{error}</div>
      )}

      {loading ? (
        <div className="text-gray-500 dark:text-gray-400">Loading…</div>
      ) : shipments.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-8 text-center">
          <p className="text-gray-700 dark:text-gray-300 font-medium">No active shipments.</p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Once one of your orders is booked with a carrier or on its way, it'll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {shipments.map((s) => {
            const isOpen = expanded.has(s.id);
            return (
              <div key={s.id} className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleExpanded(s.id)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750 flex items-center gap-3"
                >
                  <span className={`text-xs px-2 py-0.5 rounded border font-medium ${STATUS_COLOR[s.status] || ''}`}>
                    {STATUS_LABEL[s.status] || s.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {s.orderCount} order{s.orderCount === 1 ? '' : 's'} · {s.totalItemCount} item{s.totalItemCount === 1 ? '' : 's'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {STATUS_HINT[s.status]}
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-mono text-gray-900 dark:text-white">{fmt(s.freightTotalAmount)}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">total freight</div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/30">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Carrier</div>
                        <div className="text-gray-900 dark:text-white font-medium">{s.selectedCarrierName || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">BOL #</div>
                        <div className="font-mono text-xs">
                          {s.bolNumber && s.freightTrackingUrl ? (
                            <a href={s.freightTrackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{s.bolNumber} ↗</a>
                          ) : (
                            <span className="text-gray-900 dark:text-white">{s.bolNumber || '—'}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Tracking</div>
                        <div className="font-mono text-xs">
                          {(s.trackingNumber || s.proNumber) && s.freightTrackingUrl ? (
                            <a href={s.freightTrackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{s.trackingNumber || s.proNumber} ↗</a>
                          ) : (
                            <span className="text-gray-900 dark:text-white">{s.trackingNumber || s.proNumber || '—'}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Shipped</div>
                        <div className="text-gray-900 dark:text-white">{formatDate(s.shippedAt)}</div>
                      </div>
                    </div>

                    {s.boxes && s.boxes.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded mb-4">
                        <div className="px-3 py-2 border-b dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 flex justify-between">
                          <span>Box Dimensions</span>
                          <span>
                            {s.boxes.length} box{s.boxes.length === 1 ? '' : 'es'} ·{' '}
                            {s.boxes.reduce((sum, b) => sum + (b.weight || 0), 0).toFixed(0)} lbs total
                          </span>
                        </div>
                        <div className="divide-y dark:divide-gray-700">
                          {s.boxes.map((b, i) => (
                            <div key={b.id} className="px-3 py-2 flex items-center justify-between text-sm">
                              <div className="text-gray-700 dark:text-gray-300">Box {i + 1}</div>
                              <div className="font-mono text-xs text-gray-900 dark:text-white">
                                {b.length ?? '—'}" L × {b.width ?? '—'}" W × {b.height ?? '—'}" H
                                <span className="text-gray-500 dark:text-gray-400 ml-2">{b.weight != null ? `${b.weight} lbs` : ''}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded">
                      <div className="px-3 py-2 border-b dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300 flex justify-between">
                        <span>Orders in this shipment</span>
                        <span>Your share</span>
                      </div>
                      {(s.orders || []).map((o) => {
                        const canUnbundle = ['building', 'quoted'].includes(s.status) && (s.orders?.length ?? 0) > 1;
                        return (
                          <div
                            key={o.id}
                            className="px-3 py-2 border-b dark:border-gray-700 last:border-b-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-between text-sm gap-3"
                          >
                            <Link to={`/orders/${o.id}`} className="min-w-0 flex-1">
                              <div className="font-medium text-blue-600 dark:text-blue-400 truncate">
                                {o.orderNumber}
                                {o.sidemark && <span className="text-gray-500 dark:text-gray-400 font-normal"> — {o.sidemark}</span>}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {o.itemCount} item{o.itemCount === 1 ? '' : 's'} · {o.status.replace('_', ' ')}
                              </div>
                            </Link>
                            <div className="font-mono text-gray-900 dark:text-white whitespace-nowrap">{fmt(o.freightShare)}</div>
                            {canUnbundle && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!confirm(`Remove ${o.orderNumber} from this bundle? It will ship on its own as soon as it's ready (freight cost recalculated for the remaining orders).`)) return;
                                  try {
                                    await shipmentsApi.unbundleOrder(s.id, o.id);
                                    await load();
                                  } catch (err: any) {
                                    setError(err?.response?.data?.message || err.message || 'Failed to unbundle');
                                  }
                                }}
                                className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 dark:text-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30 whitespace-nowrap"
                              >
                                Ship separately
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyShipmentsPage;
